import type React from "react";
import { Fragment } from "react";

import type { QuestionDto, SurveyQuestionGroupDto } from "@/api/types";
import { FormInput } from "@/components/Form/FormInput";
import { FormStarInput } from "@/components/Form/FormStarInput";
import { Card } from "@/design-system/Card";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

import type { SurveyRespondFormValues } from "../SurveyRespondPage";
import {
  SurveyRespondFormBinaryChoiceInput,
  SurveyRespondFormMultipleChoiceInput,
} from "./SurveyRespondFormMultipleChoiceInput";
import { SurveyRespondFormRankingInput } from "./SurveyRespondFormRankingInput";

interface SurveyRespondQuestionGroupCardProps {
  questionGroup: SurveyQuestionGroupDto;
}

export function SurveyRespondQuestionGroupCard({
  questionGroup,
}: SurveyRespondQuestionGroupCardProps): React.ReactNode {
  const { t } = useTranslation();

  const title = questionGroup.questions[0].question;

  return (
    <Card as="article">
      <h3 className="text-heading-3">{title}</h3>
      <p className="text-body text-text-primary">{questionGroup.explanation}</p>

      <Stack key={questionGroup.order} gap="xl">
        {questionGroup.questions.map((question: QuestionDto) => (
          <Fragment key={question.id}>
            {question.type === "open" && (
              <FormField label={question.question} required>
                <FormInput<SurveyRespondFormValues, string>
                  data-testid="survey-respond-open-question-input"
                  name={`${question.id}.text`}
                  placeholder={t("page.survey-respond.form.placeholder.open-question")}
                  rules={{
                    required: t("components.form.error.required-generic"),
                  }}
                />
              </FormField>
            )}
            {question.type === "rating" && (
              <FormStarInput<SurveyRespondFormValues, string>
                data-testid="survey-respond-rating-input"
                name={`${question.id}.rating`}
              />
            )}
            {question.type === "yesNo" && <SurveyRespondFormBinaryChoiceInput questionId={question.id} />}
            {question.type === "multipleChoice" && (
              <SurveyRespondFormMultipleChoiceInput questionId={question.id} offeredAnswers={question.answers || []} />
            )}
            {question.type === "ranking" && (
              <SurveyRespondFormRankingInput questionId={question.id} offeredAnswers={question.answers || []} />
            )}
          </Fragment>
        ))}
      </Stack>
    </Card>
  );
}
